import axios from 'axios';
import axiosRetry from 'axios-retry';
import getConfig from 'next/config';

const { publicRuntimeConfig } = getConfig();

// Em ambientes onde o ingress-nginx-controller exige PROXY protocol (LBaaS MGC),
// o tráfego intra-cluster Pod→ExternalIP morre com ECONNRESET. Em SSR usamos a
// URL interna do cluster (process.env, server-only). No browser, mantemos o
// host público. Em produção (sem INTERNAL_API_* definida no deployment),
// o fallback mantém o host público — comportamento inalterado.
const isServer = typeof window === 'undefined';
const apiBaseSSR = process.env.INTERNAL_API_BASE || publicRuntimeConfig.NEXT_PUBLIC_API_BASE;
const mimirBaseSSR = process.env.INTERNAL_API_MIMIR || publicRuntimeConfig.NEXT_PUBLIC_API_MIMIR;
const guiaBaseSSR = process.env.INTERNAL_API_GUIA || publicRuntimeConfig.NEXT_PUBLIC_API_GUIA;

const api = axios.create({
  baseURL: `${isServer ? apiBaseSSR : publicRuntimeConfig.NEXT_PUBLIC_API_BASE}/api`,
  timeout: 10000,
  responseType: 'json',
});

const site = axios.create({
  baseURL: `${isServer ? apiBaseSSR : publicRuntimeConfig.NEXT_PUBLIC_API_BASE}/${publicRuntimeConfig.NEXT_PUBLIC_API_VERSION}`,
  timeout: 10000,
  responseType: 'json',
});

const mimir = axios.create({
  baseURL: isServer ? mimirBaseSSR : publicRuntimeConfig.NEXT_PUBLIC_API_MIMIR,
  timeout: 10000,
  responseType: 'json',
});

const guia = axios.create({
  baseURL: isServer ? guiaBaseSSR : publicRuntimeConfig.NEXT_PUBLIC_API_GUIA,
  timeout: 10000,
  responseType: 'json',
});

// Directus é M2M/SSR-only (ver directusMateria.ts, chamado só de getServerSideProps).
// Base e token vêm de process.env (server-only) — NÃO de publicRuntimeConfig, que
// é serializado no bundle e vazaria o token no browser. No client, baseURL fica
// undefined e o guard `if (!directus.defaults.baseURL) return null` curto-circuita.
const directus = axios.create({
  baseURL: process.env.DIRECTUS_BASE,
  timeout: 10000,
  responseType: 'json',
});

api.interceptors.request.use(config => {
  config.url = encodeURI(config.url);
  return config;
});

site.interceptors.request.use(config => {
  config.url = encodeURI(config.url);
  return config;
});

directus.interceptors.request.use(config => {
  config.url = encodeURI(config.url);
  config.params = { ...config.params, access_token: process.env.DIRECTUS_TOKEN };
  return config;
});

// 408 Request Timeout,
// 500 Internal Server Error,
// 502 Bad Gateway,
// 503 Service Unavailable ,
// 504 Gateway Timeout
// Se ECONNABORTED, vem do timeOut do Axios
// Se isNetworkOrIdempotentRequestError validado pelo axiosRetry

// Backoff exponencial puro (2^n * 100ms) acrescido de ±50% de jitter random.
// Diferente do `axiosRetry.exponentialDelay` (que adiciona apenas 0-20% pra
// cima), este expande o range pros dois lados — dispersa mais agressivamente
// rajadas simultâneas. Sob 100+ requests SSR concorrentes retentando ao mesmo
// tempo, o backend recebe um "thundering herd" exato a cada 200/400/800ms,
// e o colapso cascateia. ±50% transforma o spike em uma curva suave.
const RETRY_BASE_DELAY_MS = 100;
const RETRY_JITTER_RATIO = 0.5;
export function retryDelayWithJitter(retryCount: number): number {
  const base = 2 ** retryCount * RETRY_BASE_DELAY_MS;
  const jitter = base * RETRY_JITTER_RATIO * (Math.random() - 0.5) * 2;
  return Math.max(0, base + jitter);
}

const retryableHttpStatus = (status?: number): boolean => status === 408 || status === 500 || status === 502 || status === 503 || status === 504;

const shouldRetry = (error: { response?: { status?: number }; code?: string }): boolean =>
  retryableHttpStatus(error.response?.status) || error.code === 'ECONNABORTED' || axiosRetry.isNetworkOrIdempotentRequestError(error as never);

axiosRetry(api, {
  retries: 5,
  shouldResetTimeout: true,
  retryDelay: retryDelayWithJitter,
  retryCondition: shouldRetry,
});

axiosRetry(site, {
  retries: 5,
  shouldResetTimeout: true,
  retryDelay: retryDelayWithJitter,
  retryCondition: shouldRetry,
});

axiosRetry(directus, {
  retries: 3,
  shouldResetTimeout: true,
  retryDelay: retryDelayWithJitter,
  retryCondition: shouldRetry,
});

axiosRetry(mimir, {
  retries: 5,
  shouldResetTimeout: true,
  retryDelay: retryDelayWithJitter,
  retryCondition: shouldRetry,
});

const validatesResponse = (
  dataResponseAPI,
  isCatch = false,
  isCategory = false,
  category: string | string[] = '',
  host = null,
  prevHost = null,
  pathName = null
) => {
  // Usando o CT status que são:
  // case 0: //sucesso(200)
  // case 1: //erro no servidor(500)
  // case 2: //acesso negado(403)
  // case 3: //não encontrado(404)
  // case 4: //inconsistencia(400)
  // case 5: //metodo não permitido(405)
  // case 6: //redirect(301)
  // case 7: //redirect(301)

  let data = null;
  let statusCode = 0;

  if (!isCatch && dataResponseAPI) {
    data = dataResponseAPI.data;
    statusCode = dataResponseAPI.status;
    if (data?.status == null) {
      return { data, statusCode };
    }
  }

  switch (isCatch ? dataResponseAPI.response?.data?.status : dataResponseAPI.data.status) {
    case 3:
      if (!isCategory) {
        data = {
          notFound: true,
          props: {},
        };
      } else {
        data = {
          redirect: {
            statusCode: 301,
            destination: `${publicRuntimeConfig.NEXT_PUBLIC_URL}/${category}/`,
          },
          props: {},
        };
      }
      break;
    case 8:
      return {
        data: {
          headers: isCatch ? dataResponseAPI.response.data.headers : data.headers,
        },
        statusCode: isCatch ? dataResponseAPI.response.data.data.codigo : data.data.codigo,
      };
      break;
    case 1:
    case 2:
    case 4:
    case 5:
      statusCode = isCatch ? dataResponseAPI.response.data.data.codigo : data.data.codigo;
      break;
    case 6:
    case 7:
      data = {
        redirect: {
          statusCode: isCatch ? dataResponseAPI.response.data.data.codigo : data.data.codigo,
          destination: isCatch ? dataResponseAPI.response.data.data.url : data.data.url,
        },
        props: {},
      };
      break;
    default:
      if (isCatch) throw dataResponseAPI;
      statusCode = dataResponseAPI.status;
  }

  const localHost = publicRuntimeConfig.NEXT_PUBLIC_HOST;
  const desiredHost = dataResponseAPI?.data?.data?.dominio || localHost;

  // Não redireciona se já estamos servindo o host canônico deste deploy.
  // No preview (host = directus.hml.staging-canaltech.com.br) a API consumida é a do
  // staging, que declara `dominio = staging-canaltech.com.br`; sem este guard toda página
  // de conteúdo era expulsa pro staging. Prod/staging seguem canonicalizando hosts estrangeiros.
  if (host?.toString() !== desiredHost?.toString() && host?.toString() !== localHost?.toString()) {
    data = {
      redirect: {
        statusCode: 301,
        destination: `https://${desiredHost}${dataResponseAPI?.data?.data?.url || pathName || ''}`,
      },
      props: {},
    };
  }

  // Erros de servidor/acesso (status 1/2/4/5) no caminho isCatch só setam statusCode e deixam
  // `data` nulo. Garante shape válido para os callers não estourarem em data.redirect. (FAWKES-37R)
  if (!data) {
    data = { notFound: true, props: {} };
  }

  return { data, statusCode };
};

export { api, site, validatesResponse, mimir, guia, directus };
